/**
 * Detect which Ultimate / Godly Setup foundation pieces already exist for a World.
 * Mirrors COCKPIT_CHECKS logic where applicable so cockpit % and setup diagnosis stay aligned.
 */

export const FOUNDATION_ORDER = ['vault', 'cleaner', 'safeGuard', 'templates', 'contacts'];

const hasKeys = (obj) => obj && typeof obj === 'object' && Object.keys(obj).length > 0;

const objectLength = (obj) => (obj && typeof obj === 'object' ? Object.keys(obj).length : 0);

/**
 * @param {object|null} data — cockpit snapshot from useCockpitData (must include vaultEntryCount when loaded)
 */
export function deriveUltimateFoundationStatus(data) {
  if (!data) {
    return {
      checks: {},
      missing: [],
      filled: [],
      coreFoundationDone: false,
      allDone: false,
      filledCount: 0,
      totalCount: FOUNDATION_ORDER.length,
      summaryLine: '',
    };
  }

  const vaultOk = Number(data.vaultEntryCount || 0) >= 1;
  const cleanerOk = hasKeys(data.cleanerGuidelines)
    || !!(data.aiSettings?.cleanupGuidelines && String(data.aiSettings.cleanupGuidelines).trim());
  const safeGuardOk = hasKeys(data.safeGuardSettings);
  const templatesOk = objectLength(data.selectedTopics) + objectLength(data.customTopics) >= 1;
  const contactsOk = !!data.hasLeads;

  const checks = {
    vault: {
      ok: vaultOk,
      label: 'Long-term brain (Vault)',
      pillShort: 'Vault',
      tab: 'dataVault',
      icon: 'fa-brain',
    },
    cleaner: {
      ok: cleanerOk,
      label: 'Cleaner rules',
      pillShort: 'Cleaner',
      tab: 'ai',
      icon: 'fa-filter',
    },
    safeGuard: {
      ok: safeGuardOk,
      label: 'SafeGuard',
      pillShort: 'SafeGuard',
      tab: 'safeGuard',
      icon: 'fa-shield-halved',
    },
    templates: {
      ok: templatesOk,
      label: 'Email templates',
      pillShort: 'Templates',
      tab: 'topics',
      icon: 'fa-pen-to-square',
    },
    contacts: {
      ok: contactsOk,
      label: 'Contacts in database',
      pillShort: 'Contacts',
      tab: 'worlddb',
      icon: 'fa-address-book',
    },
  };

  const missing = FOUNDATION_ORDER.filter((id) => !checks[id].ok).map((id) => ({ id, ...checks[id] }));
  const filled = FOUNDATION_ORDER.filter((id) => checks[id].ok).map((id) => ({ id, ...checks[id] }));

  const coreFoundationDone = vaultOk && cleanerOk && safeGuardOk && templatesOk;
  const allDone = coreFoundationDone && contactsOk;

  let summaryLine = '';
  if (allDone) {
    summaryLine = 'Foundation looks complete: you can still rerun setup to refresh everything from a new description.';
  } else if (missing.length === 0) {
    summaryLine = '';
  } else if (missing.length <= 2) {
    summaryLine = `Still open: ${missing.map((m) => m.label).join(' · ')}.`;
  } else {
    summaryLine = `${missing.length} foundation items still open; see pills below.`;
  }

  return {
    checks,
    missing,
    filled,
    coreFoundationDone,
    allDone,
    filledCount: filled.length,
    totalCount: FOUNDATION_ORDER.length,
    summaryLine,
  };
}
